/**
 * إعدادات تخطيط الهوم على الديسكتوب (HomeDesktopEditorial).
 * تُخزَّن في app_settings تحت key="home_desktop_editorial".
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./adminAuth.server";
import { HOME_DESKTOP_KEY, normalizeHomeDesktopConfig } from "@/lib/home-desktop-config";

const schema = z.object({
  statement: z.object({
    enabled: z.boolean(),
    line1Ar: z.string().max(200),
    line1En: z.string().max(200),
    line2Ar: z.string().max(200),
    line2En: z.string().max(200),
    bodyAr: z.string().max(600),
    bodyEn: z.string().max(600),
    ctaLabelAr: z.string().max(80),
    ctaLabelEn: z.string().max(80),
    ctaHref: z.string().max(500),
    mediaUrl: z.string().max(800),
  }),
  spotlight: z.object({
    enabled: z.boolean(),
    productHandle: z.string().max(200),
    altHandles: z.array(z.string().max(200)).max(6).default([]),
    mediaUrl: z.string().max(800),
    notesAr: z.string().max(800),
    notesEn: z.string().max(800),
    ctaLabelAr: z.string().max(80),
    ctaLabelEn: z.string().max(80),
    showUpgrade: z.boolean(),
    upgradeHandle: z.string().max(200),
  }),
  fragrances: z.object({
    enabled: z.boolean(),
    titleAr: z.string().max(200),
    titleEn: z.string().max(200),
    handles: z.array(z.string().max(200)).max(8),
  }),
  outro: z.object({
    captionAr: z.string().max(400),
    captionEn: z.string().max(400),
    href: z.string().max(500),
  }),
});

export const getHomeDesktopConfig = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({}).parse(d ?? {}))
  .handler(async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", HOME_DESKTOP_KEY)
      .maybeSingle();
    return { config: normalizeHomeDesktopConfig(data?.value ?? null) };
  });

export const setHomeDesktopConfig = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => schema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const value = normalizeHomeDesktopConfig(data);
    const { error } = await supabaseAdmin.from("app_settings").upsert(
      {
        key: HOME_DESKTOP_KEY,
        value,
        updated_by: context.userId,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "key" },
    );
    if (error) throw new Error(error.message);
    return { ok: true, config: value };
  });
